import React from "react";
import { tridotfooterlogo } from "../../images";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  ExternalLink,
} from "lucide-react";

interface LinkItem {
  text: string;
  href: string;
}

interface FooterColumnProps {
  title: string;
  links: LinkItem[];
}

const FooterColumn: React.FC<FooterColumnProps> = ({ title, links }) => (
  <div className="flex flex-col space-y-4">
    <h3 className="font-semibold text-white text-lg">{title}</h3>
    <ul className="space-y-3">
      {links.map((link, index) => (
        <li key={index}>
          <a
            href={link.href}
            className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center gap-1 group">
            {link.text}
            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const SocialLink: React.FC<{ href: string; icon: React.ReactNode }> = ({
  href,
  icon,
}) => (
  <a
    href={href}
    className="text-gray-400 hover:text-white transition-colors duration-200"
    target="_blank"
    rel="noopener noreferrer">
    {icon}
  </a>
);

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks: LinkItem[] = [
    { text: "Home", href: "#" },
    { text: "About", href: "#" },
    { text: "Products", href: "#" },
    { text: "Contact", href: "#" },
  ];

  const resources: LinkItem[] = [
    { text: "FAQs", href: "#" },
    { text: "Pricing", href: "#" },
    { text: "Partners", href: "#" },
    { text: "Support", href: "#" },
  ];

  const legal: LinkItem[] = [
    { text: "Terms of Service", href: "#" },
    { text: "Privacy Policy", href: "#" },
    { text: "Refund Policy", href: "#" },
    { text: "Cookie Policy", href: "#" },
  ];

  return (
    <footer className="bg-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Logo and Description Section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center">
              <img
                src={tridotfooterlogo}
                alt="Tridot Logo"
                className="h-14 w-auto transition-transform hover:scale-105"
              />
            </div>
            <p className="text-gray-300 max-w-sm">
              Empowering education through innovative technology solutions for
              modern Tridots.
            </p>
            {/* Social Links */}
            <div className="flex space-x-4">
              <SocialLink href="#" icon={<Facebook className="w-6 h-6" />} />
              <SocialLink href="#" icon={<Twitter className="w-6 h-6" />} />
              <SocialLink href="#" icon={<Instagram className="w-6 h-6" />} />
              <SocialLink href="#" icon={<Linkedin className="w-6 h-6" />} />
            </div>
          </div>

          {/* Footer Columns */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-8">
            <FooterColumn title="Quick Links" links={quickLinks} />
            <FooterColumn title="Resources" links={resources} />
            <FooterColumn title="Legal" links={legal} />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-gray-700">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} Tridot, Inc. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-gray-400 hover:text-white text-sm">
                Terms
              </a>
              <a href="#" className="text-gray-400 hover:text-white text-sm">
                Privacy
              </a>
              <a href="#" className="text-gray-400 hover:text-white text-sm">
                Cookies
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
